/**
 * Grade equivalency — client-side mirror of
 * `/app/backend/data/grade_equivalency.py`.
 *
 * The backend module is the source of truth (public endpoint at
 * `/api/grade-equivalency` for partner embeds). This mirror exists so
 * the Grade Calculator page can update every field live-as-you-type
 * without a network round-trip. A backend test verifies both tables
 * produce the same output at anchor points.
 */

const _ANCHORS = [
  { tier: "EEE",    a_level_rank:  3, ib: 20, atar: 40.0,  gpa: 2.00, cbse: 55.0, isc: 53.0, ap_composite: 1.00, abitur: 4.0, french_bac:  8.0, hkdse_best5: 10, suneung_pct: 40.0, eju_top3: 260, sat: 800, act: 14, ucas: 48 },
  { tier: "DDE",    a_level_rank:  5, ib: 21, atar: 45.0,  gpa: 2.35, cbse: 60.0, isc: 58.0, ap_composite: 1.50, abitur: 3.7, french_bac:  9.0, hkdse_best5: 12, suneung_pct: 45.0, eju_top3: 300, sat: 860, act: 15, ucas: 64 },
  { tier: "DDD",    a_level_rank:  6, ib: 22, atar: 50.0,  gpa: 2.50, cbse: 65.0, isc: 63.0, ap_composite: 2.00, abitur: 3.5, french_bac: 10.0, hkdse_best5: 14, suneung_pct: 50.0, eju_top3: 330, sat: 900, act: 16, ucas: 72 },
  { tier: "CDD",    a_level_rank:  7, ib: 24, atar: 58.0,  gpa: 2.75, cbse: 70.0, isc: 68.0, ap_composite: 2.50, abitur: 3.2, french_bac: 11.0, hkdse_best5: 16, suneung_pct: 58.0, eju_top3: 370, sat: 950, act: 17, ucas: 80 },
  { tier: "CCD",    a_level_rank:  8, ib: 25, atar: 62.0,  gpa: 2.85, cbse: 72.0, isc: 70.0, ap_composite: 2.70, abitur: 3.0, french_bac: 11.5, hkdse_best5: 18, suneung_pct: 62.0, eju_top3: 400, sat: 990, act: 18, ucas: 88 },
  { tier: "CCC",    a_level_rank:  9, ib: 26, atar: 65.0,  gpa: 3.00, cbse: 75.0, isc: 73.0, ap_composite: 3.00, abitur: 2.7, french_bac: 12.0, hkdse_best5: 20, suneung_pct: 65.0, eju_top3: 430, sat: 1030, act: 19, ucas: 96 },
  { tier: "BCC",    a_level_rank: 10, ib: 28, atar: 68.0,  gpa: 3.20, cbse: 78.0, isc: 76.0, ap_composite: 3.20, abitur: 2.5, french_bac: 13.0, hkdse_best5: 22, suneung_pct: 70.0, eju_top3: 470, sat: 1090, act: 20, ucas: 104 },
  { tier: "BBC",    a_level_rank: 11, ib: 30, atar: 72.0,  gpa: 3.35, cbse: 82.0, isc: 80.0, ap_composite: 3.40, abitur: 2.3, french_bac: 13.5, hkdse_best5: 24, suneung_pct: 75.0, eju_top3: 500, sat: 1150, act: 22, ucas: 112 },
  { tier: "BBB",    a_level_rank: 12, ib: 33, atar: 78.0,  gpa: 3.50, cbse: 85.0, isc: 83.0, ap_composite: 3.50, abitur: 2.0, french_bac: 14.0, hkdse_best5: 25, suneung_pct: 80.0, eju_top3: 540, sat: 1200, act: 24, ucas: 120 },
  { tier: "ABB",    a_level_rank: 13, ib: 35, atar: 84.0,  gpa: 3.65, cbse: 87.0, isc: 85.0, ap_composite: 3.80, abitur: 1.8, french_bac: 15.0, hkdse_best5: 27, suneung_pct: 84.0, eju_top3: 580, sat: 1260, act: 26, ucas: 128 },
  { tier: "AAB",    a_level_rank: 14, ib: 37, atar: 88.0,  gpa: 3.75, cbse: 90.0, isc: 88.0, ap_composite: 4.00, abitur: 1.6, french_bac: 15.5, hkdse_best5: 29, suneung_pct: 88.0, eju_top3: 610, sat: 1320, act: 28, ucas: 136 },
  { tier: "AAA",    a_level_rank: 15, ib: 39, atar: 92.0,  gpa: 3.85, cbse: 93.0, isc: 91.0, ap_composite: 4.50, abitur: 1.5, french_bac: 16.0, hkdse_best5: 30, suneung_pct: 92.0, eju_top3: 650, sat: 1380, act: 30, ucas: 144 },
  { tier: "A*AA",   a_level_rank: 16, ib: 41, atar: 96.0,  gpa: 3.90, cbse: 95.0, isc: 93.0, ap_composite: 4.70, abitur: 1.3, french_bac: 17.0, hkdse_best5: 32, suneung_pct: 95.0, eju_top3: 690, sat: 1450, act: 32, ucas: 160 },
  { tier: "A*A*A",  a_level_rank: 17, ib: 43, atar: 98.0,  gpa: 3.95, cbse: 97.0, isc: 94.5,ap_composite: 4.85, abitur: 1.1, french_bac: 18.0, hkdse_best5: 34, suneung_pct: 97.0, eju_top3: 720, sat: 1520, act: 34, ucas: 168 },
  { tier: "A*A*A*", a_level_rank: 18, ib: 45, atar: 99.95, gpa: 4.00, cbse: 98.0, isc: 96.0, ap_composite: 5.00, abitur: 1.0, french_bac: 19.5, hkdse_best5: 35, suneung_pct: 99.0, eju_top3: 760, sat: 1560, act: 35, ucas: 168 },
];

const _INVERTED_CURRICULA = new Set(["abitur"]);

const _LETTER_PTS = { "A*": 6, A: 5, B: 4, C: 3, D: 2, E: 1 };

const _COL = {
  a_level: "a_level_rank",
  ib: "ib",
  atar: "atar",
  gpa: "gpa",
  american_diploma: "gpa",
  cbse: "cbse",
  isc: "isc",
  ap_composite: "ap_composite",
  abitur: "abitur",
  french_bac: "french_bac",
  hkdse: "hkdse_best5",
  suneung: "suneung_pct",
  eju: "eju_top3",
  sat: "sat",
  act: "act",
  // UCAS Tariff points — valid input curriculum so students who were
  // given a points offer (e.g. "128 UCAS points") can work backwards
  // into A-level / IB / ATAR equivalents.
  ucas: "ucas",
};

export const CURRICULUM_LABELS = {
  a_level: "A Levels (UK)",
  ib: "IB Diploma",
  atar: "ATAR (Australia)",
  gpa: "GPA (US 4.0)",
  sat: "SAT total (US, 400-1600)",
  act: "ACT composite (US, 1-36)",
  cbse: "CBSE (India, %)",
  isc: "ISC / CISCE (India, %)",
  ap_composite: "AP composite (US)",
  abitur: "Abitur (Germany)",
  french_bac: "Baccalauréat (France, /20)",
  hkdse: "HKDSE (Hong Kong, best-5)",
  suneung: "Suneung / CSAT (Korea, %)",
  eju: "EJU (Japan, top-3 / 800)",
  ucas: "UCAS Tariff points",
};

export function parseAlevel(s) {
  if (!s) return null;
  const t = String(s).toUpperCase().trim().replace(/[\s,]/g, "");
  let total = 0;
  let i = 0;
  let count = 0;
  while (i < t.length) {
    if (t[i] === "A" && t[i + 1] === "*") {
      total += 6;
      i += 2;
    } else if (_LETTER_PTS[t[i]]) {
      total += _LETTER_PTS[t[i]];
      i += 1;
    } else {
      return null;
    }
    count += 1;
  }
  return count === 3 ? total : null;
}

function _rankToAlevelStr(rank) {
  if (rank <= 0) return "EEE";
  let best = _ANCHORS[0];
  let bestD = Math.abs(_ANCHORS[0].a_level_rank - rank);
  for (const a of _ANCHORS) {
    const d = Math.abs(a.a_level_rank - rank);
    if (d < bestD) { best = a; bestD = d; }
  }
  return best.tier;
}

function _interp(x, xs, ys) {
  if (x <= xs[0]) return ys[0];
  if (x >= xs[xs.length - 1]) return ys[ys.length - 1];
  for (let i = 1; i < xs.length; i++) {
    if (xs[i] >= x) {
      const x0 = xs[i - 1], x1 = xs[i], y0 = ys[i - 1], y1 = ys[i];
      if (x1 === x0) return y0;
      return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
    }
  }
  return ys[ys.length - 1];
}

function _sourceValueToNumeric(curriculum, value) {
  if (curriculum === "a_level") {
    if (typeof value === "number") return value;
    return parseAlevel(String(value));
  }
  const v = Number(value);
  if (Number.isNaN(v)) return null;
  if (curriculum === "ib") return v >= 0 && v <= 45 ? v : null;
  if (curriculum === "atar") return v >= 0 && v <= 99.95 ? v : null;
  if (curriculum === "gpa" || curriculum === "american_diploma") return v >= 0 && v <= 4.5 ? v : null;
  if (curriculum === "cbse" || curriculum === "isc") return v >= 0 && v <= 100 ? v : null;
  if (curriculum === "ap_composite") return v >= 1.0 && v <= 5.05 ? v : null;
  if (curriculum === "abitur") return v >= 1.0 && v <= 4.0 ? v : null;
  if (curriculum === "french_bac") return v >= 0 && v <= 20 ? v : null;
  if (curriculum === "hkdse") return v >= 0 && v <= 35 ? v : null;
  if (curriculum === "suneung") return v >= 0 && v <= 100 ? v : null;
  if (curriculum === "eju") return v >= 0 && v <= 800 ? v : null;
  if (curriculum === "sat") return v >= 400 && v <= 1600 ? v : null;
  if (curriculum === "act") return v >= 1 && v <= 36 ? v : null;
  // UCAS Tariff — anchors run 48 (EEE) to 168 (A*A*A*). Allow 0-200
  // so partial / supplementary points don't get rejected outright.
  if (curriculum === "ucas") return v >= 0 && v <= 200 ? v : null;
  return null;
}

/**
 * Return the equivalents of `value` in every other curriculum.
 * Returns `null` on unparseable input so the caller can render a hint.
 */
export function equivalentsFor(curriculum, value) {
  if (!(curriculum in _COL)) return null;
  const num = _sourceValueToNumeric(curriculum, value);
  if (num == null) return null;

  const srcCol = _COL[curriculum];
  const srcInverted = _INVERTED_CURRICULA.has(curriculum);
  const xsRaw = _ANCHORS.map((a) => a[srcCol]);
  const xs = srcInverted ? [...xsRaw].reverse() : xsRaw;

  const equivalents = {};
  for (const [cur, col] of Object.entries(_COL)) {
    if (cur === "american_diploma") continue;
    if (cur === curriculum) continue;  // don't echo the input back as its own equivalent
    const ysRaw = _ANCHORS.map((a) => a[col]);
    const ys = srcInverted ? [...ysRaw].reverse() : ysRaw;
    const val = _interp(num, xs, ys);
    if (cur === "a_level") equivalents.a_level = _rankToAlevelStr(val);
    else if (cur === "ib") equivalents.ib = Math.round(val * 10) / 10;
    else if (cur === "atar") equivalents.atar = Math.round(val * 100) / 100;
    else if (cur === "gpa") equivalents.gpa = Math.round(val * 100) / 100;
    else if (cur === "cbse") equivalents.cbse = Math.round(val * 10) / 10;
    else if (cur === "isc") equivalents.isc = Math.round(val * 10) / 10;
    else if (cur === "ap_composite") equivalents.ap_composite = Math.round(val * 100) / 100;
    else if (cur === "abitur") equivalents.abitur = Math.round(val * 10) / 10;
    else if (cur === "french_bac") equivalents.french_bac = Math.round(val * 10) / 10;
    else if (cur === "hkdse") equivalents.hkdse = Math.round(val);
    else if (cur === "suneung") equivalents.suneung = Math.round(val * 10) / 10;
    else if (cur === "eju") equivalents.eju = Math.round(val);
    else if (cur === "sat") equivalents.sat = Math.round(val / 10) * 10;
    else if (cur === "act") equivalents.act = Math.round(val);
    else if (cur === "ucas") equivalents.ucas = Math.round(val);
  }
  // `ucas` isn't in `_COL` for backwards compatibility of callers that
  // read it from `_ANCHORS` directly — but we also populate it in the
  // loop above when the source isn't UCAS. Nothing to do here.

  let nearestTier = _ANCHORS[0].tier;
  let bestD = Infinity;
  for (const a of _ANCHORS) {
    const d = Math.abs(a[srcCol] - num);
    if (d < bestD) { nearestTier = a.tier; bestD = d; }
  }
  return {
    input: { curriculum, value, tier: nearestTier },
    equivalents,
    notes: "Mappings are indicative — check the specific course page for the authoritative offer.",
  };
}

/** Full anchor table for the "universities at each tier" showcase. */
export const ANCHOR_TIERS = _ANCHORS.map((a) => ({ ...a }));

/** Named selectivity tiers used in the university-showcase strip. */
export const SHOWCASE_TIERS = [
  { label: "Oxbridge / Ivy",       tier: "A*A*A"  },
  { label: "Russell Group / Go8",  tier: "AAA"    },
  { label: "Solid research",       tier: "AAB"    },
  { label: "Mid-tier UK / US",     tier: "BBB"    },
  { label: "Widening participation", tier: "CDD"  },
];

// ── Reverse-tariff grade finder ─────────────────────────────────

const _A_LEVEL_POINTS = [
  { letter: "A*", pts: 56 },
  { letter: "A",  pts: 48 },
  { letter: "B",  pts: 40 },
  { letter: "C",  pts: 32 },
  { letter: "D",  pts: 24 },
  { letter: "E",  pts: 16 },
];

const _IB_HL_POINTS = [
  { letter: "7", pts: 56 },
  { letter: "6", pts: 48 },
  { letter: "5", pts: 32 },
  { letter: "4", pts: 24 },
  { letter: "3", pts: 12 },
];

/**
 * `tariffCombos(target, { tolerance })` — given a UCAS Tariff target,
 * return the A-level and IB-HL triplets whose points sum to the target
 * (or within ±`tolerance`). Combinations are returned non-increasing
 * so "A*AA" never shows up as "AA*A" too, keeping the list clean.
 *
 * Why only 3-subject combos?
 * UK offers almost always quote 3 A-levels (or 3 HL subjects for IB).
 * Showing 4-A-level permutations would inflate the list without helping
 * a student decide what grades to aim for.
 */
export function tariffCombos(target, { tolerance = 0 } = {}) {
  const t = Number(target);
  if (!Number.isFinite(t) || t <= 0) return { aLevel: [], ib: [] };

  const build = (table) => {
    const results = [];
    // Iterate i ≤ j ≤ k over the table (which is sorted descending by
    // points). That means index(i) ≤ index(j) ≤ index(k) ⇒ grades are
    // non-increasing, no duplicates.
    for (let i = 0; i < table.length; i++) {
      for (let j = i; j < table.length; j++) {
        for (let k = j; k < table.length; k++) {
          const total = table[i].pts + table[j].pts + table[k].pts;
          if (Math.abs(total - t) <= tolerance) {
            results.push({
              letters: `${table[i].letter}${table[j].letter}${table[k].letter}`,
              points: total,
              delta:  total - t,
            });
          }
        }
      }
    }
    // Exact-matches first, then nearest, then by grade strength (highest total).
    results.sort((a, b) => (
      Math.abs(a.delta) - Math.abs(b.delta)
      || b.points - a.points
    ));
    return results;
  };

  return {
    aLevel: build(_A_LEVEL_POINTS),
    ib:     build(_IB_HL_POINTS),
  };
}

